import { Component, OnInit, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PaymentGatewayConfigDto, PaymentGatewayProviderDto } from '../../_core/_model/payment-admin';
import { ApiService } from '../../_core/_services/api.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { Modal } from 'bootstrap';
import { MiniCountryDto } from '../../_core/_model/countries';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-payment-gateway-configs',
  standalone: false,
  templateUrl: './payment-gateway-configs.html',
  styleUrl: './payment-gateway-configs.css',
})

export class PaymentGatewayConfigs implements OnInit {
  loading = false;
  submitting = false;
  modalSubmitting = false;

  countries: MiniCountryDto[] = [];

  providers: PaymentGatewayProviderDto[] = [];
  configs: PaymentGatewayConfigDto[] = [];
  filteredConfigs: PaymentGatewayConfigDto[] = [];

  search = '';
  editingId: string | null = null;

  editModal?: Modal;

  createForm: FormGroup;
  editForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private notify: NotificationService
  ) {
    this.createForm = this.fb.group({
      providerId: ['', Validators.required],
      name: ['', Validators.required],
      environment: ['Live', Validators.required],
      countryCodes: [[], Validators.required],
      publicKey: [''],
      secretKey: [''],
      encryptionKey: [''],
      merchantId: [''],
      webhookSecret: [''],
      baseUrl: [''],
      metadataJson: ['']
    });
    this.editForm = this.fb.group({
      providerId: ['', Validators.required],
      name: ['', Validators.required],
      environment: ['Live', Validators.required],
      countryCode: [''],
      publicKey: [''],
      secretKey: [''],
      encryptionKey: [''],
      merchantId: [''],
      webhookSecret: [''],
      baseUrl: [''],
      metadataJson: [''],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadMain();
    this.loadProviders();
    this.loadConfigs();
  }

  loadMain() {
    this.loading = true;

    this.api.getMain().subscribe({
      next: (res) => {
        this.countries = (res.data?.countries ?? []).filter(c => c.status === 'Active');
      },

      error: (e) => {
        this.notify.showError(
          e?.error?.Error?.Details || 'Failed to load Currency and Country data',
          'Error'
        );
      },
      complete: () => (this.loading = false),
    });
  }

  loadProviders(): void {
    this.api.getPaymentGatewayProviders().subscribe({
      next: (res) => {
        this.providers = res.data || [];
      },
      error: (e) => {
        this.notify.showError(
          e?.error?.Error?.Details || 'Failed to load providers',
          'Error'
        );
      }
    });
  }

  loadConfigs(): void {
    this.loading = true;
    this.api.getPaymentGatewayConfigs().subscribe({
      next: (res) => {
        this.configs = res.data || [];
        this.applyFilter();
      },
      error: (e) => {
        this.notify.showError(
          e?.error?.Error?.Details || 'Failed to load configs',
          'Error'
        );
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  applyFilter(): void {
    const q = this.search.trim().toLowerCase();

    this.filteredConfigs = !q
      ? [...this.configs]
      : this.configs.filter(item =>
        item.providerName.toLowerCase().includes(q) ||
        item.providerCode.toLowerCase().includes(q) ||
        item.name.toLowerCase().includes(q) ||
        item.environment.toLowerCase().includes(q) ||
        (item.countryCode || '').toLowerCase().includes(q)
      );
  }

  create(): void {
    if (this.createForm.invalid) return;

    if (!this.validateBlaaizMetadata('create')) {
      return;
    }

    const countryCodes: string[] = this.createForm.value.countryCodes || [];

    if (countryCodes.length === 0) {
      this.notify.showError('Please select at least one country', 'Error');
      return;
    }

    this.submitting = true;

    const baseName = (this.createForm.value.name || '').trim();

    const requests = countryCodes.map(countryCode => {
      const normalizedCountryCode = this.normalizeUpper(countryCode);

      const payload = {
        providerId: this.createForm.value.providerId || '',
        name: countryCodes.length > 1
          ? `${baseName} - ${normalizedCountryCode}`
          : baseName,
        environment: this.createForm.value.environment || 'Live',
        countryCode: normalizedCountryCode,
        publicKey: this.normalizeEmpty(this.createForm.value.publicKey),
        secretKey: this.normalizeEmpty(this.createForm.value.secretKey),
        encryptionKey: this.normalizeEmpty(this.createForm.value.encryptionKey),
        merchantId: this.normalizeEmpty(this.createForm.value.merchantId),
        webhookSecret: this.normalizeEmpty(this.createForm.value.webhookSecret),
        baseUrl: this.normalizeEmpty(this.createForm.value.baseUrl),
        metadataJson: this.normalizeEmpty(this.createForm.value.metadataJson)
      };

      return this.api.createPaymentGatewayConfig(payload);
    });

    forkJoin(requests).subscribe({
      next: () => {
        this.notify.showSuccess(
          countryCodes.length > 1
            ? 'Gateway configs created successfully'
            : 'Gateway config created successfully',
          'Success'
        );

        this.createForm.reset({
          providerId: '',
          name: '',
          environment: 'Live',
          countryCodes: [],
          publicKey: '',
          secretKey: '',
          encryptionKey: '',
          merchantId: '',
          webhookSecret: '',
          baseUrl: '',
          metadataJson: ''
        });

        this.loadConfigs();
      },
      error: (e) => {
        this.notify.showError(
          e?.error?.Error?.Details || 'Failed to create one or more configs',
          'Error'
        );
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }

  openEditModal(item: PaymentGatewayConfigDto): void {
    this.editingId = item.id;

    this.editForm.patchValue({
      providerId: item.providerId,
      name: item.name,
      environment: item.environment,
      countryCode: item.countryCode || '',
      publicKey: '',
      secretKey: '',
      encryptionKey: '',
      merchantId: '',
      webhookSecret: '',
      baseUrl: item.baseUrl || '',
      metadataJson: item.metadataJson || '',
      isActive: item.isActive
    });


    // show modal
    const el = document.getElementById('editConfigModal');
    if (!el) return;

    this.editModal = Modal.getOrCreateInstance(el);
    this.editModal.show();
  }

  saveEdit(): void {
    if (!this.editingId || this.editForm.invalid) return;

    if (!this.validateBlaaizMetadata('edit')) {
      return;
    }

    this.modalSubmitting = true;

    const payload = {
      providerId: this.editForm.value.providerId || '',
      name: (this.editForm.value.name || '').trim(),
      environment: this.editForm.value.environment || 'Live',
      countryCode: this.normalizeUpper(this.editForm.value.countryCode),
      publicKey: this.normalizeEmpty(this.editForm.value.publicKey),
      secretKey: this.normalizeEmpty(this.editForm.value.secretKey),
      encryptionKey: this.normalizeEmpty(this.editForm.value.encryptionKey),
      merchantId: this.normalizeEmpty(this.editForm.value.merchantId),
      webhookSecret: this.normalizeEmpty(this.editForm.value.webhookSecret),
      baseUrl: this.normalizeEmpty(this.editForm.value.baseUrl),
      metadataJson: this.normalizeEmpty(this.editForm.value.metadataJson),
      isActive: !!this.editForm.value.isActive
    };

    this.api.updatePaymentGatewayConfig(this.editingId, payload).subscribe({
      next: () => {
        this.notify.showSuccess('Gateway config updated successfully', 'Success');
        this.editModal?.hide();
        this.loadConfigs();
      },
      error: (e) => {
        this.notify.showError(
          e?.error?.Error?.Details || 'Failed to update config',
          'Error'
        );
      },
      complete: () => {
        this.modalSubmitting = false;
      }
    });
  }

  toggleStatus(item: PaymentGatewayConfigDto): void {
    this.api.togglePaymentGatewayConfigStatus(item.id).subscribe({
      next: () => {
        this.notify.showSuccess('Gateway config status updated successfully', 'Success');
        this.loadConfigs();
      },
      error: (e) => {
        this.notify.showError(
          e?.error?.Error?.Details || 'Failed to update config status',
          'Error'
        );
      }
    });
  }

  private normalizeEmpty(value: string | null | undefined): string | null {
    const v = (value || '').trim();
    return v ? v : null;
  }

  private normalizeUpper(value: string | null | undefined): string | null {
    const v = (value || '').trim().toUpperCase();
    return v ? v : null;
  }

  isBlaaizProvider(providerId: string | null | undefined): boolean {
    if (!providerId) return false;

    const provider = this.providers.find(x => x.id === providerId);
    return (provider?.code || '').toUpperCase() === 'BLAAIZ';
  }

  getBlaaizMetadataTemplate(): string {
    return JSON.stringify({
      interacEmail: '',
      cadWalletId: '',
      usdWalletId: '',
      gbpWalletId: '',
      eurWalletId: '',
      cadCurrencyId: 'CAD',
      usdCurrencyId: 'USD',
      gbpCurrencyId: 'GBP',
      eurCurrencyId: 'EUR'
    }, null, 2);
  }

  applyBlaaizMetadataTemplate(target: 'create' | 'edit'): void {
    const form = target === 'create' ? this.createForm : this.editForm;

    form.patchValue({
      metadataJson: this.getBlaaizMetadataTemplate()
    });
  }

  formatMetadataJson(target: 'create' | 'edit'): void {
    const form = target === 'create' ? this.createForm : this.editForm;
    const value = form.value.metadataJson;

    if (!value) return;

    try {
      const parsed = JSON.parse(value);
      form.patchValue({
        metadataJson: JSON.stringify(parsed, null, 2)
      });
    } catch {
      this.notify.showError('Metadata Json is not valid JSON.', 'Invalid JSON');
    }
  }

  validateBlaaizMetadata(target: 'create' | 'edit'): boolean {
    const form = target === 'create' ? this.createForm : this.editForm;

    if (!this.isBlaaizProvider(form.value.providerId)) {
      return true;
    }

    const raw = form.value.metadataJson;

    if (!raw) {
      this.notify.showError('Blaaiz Metadata Json is required.', 'Validation');
      return false;
    }

    let parsed: any;

    try {
      parsed = JSON.parse(raw);
    } catch {
      this.notify.showError('Blaaiz Metadata Json must be valid JSON.', 'Validation');
      return false;
    }

    const requiredKeys = [
      'interacEmail',
      'cadWalletId',
      'usdWalletId',
      'gbpWalletId',
      'eurWalletId',
      'cadCurrencyId',
      'usdCurrencyId',
      'gbpCurrencyId',
      'eurCurrencyId'
    ];

    const missing = requiredKeys.filter(k => !parsed[k]);

    if (missing.length > 0) {
      this.notify.showError(
        `Missing Blaaiz metadata keys: ${missing.join(', ')}`,
        'Validation'
      );
      return false;
    }

    return true;
  }
}